import React, { useState, useEffect } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import { PromptTemplate, UserPromptAnswer } from '../../../types/profile';

interface Stage5Props extends StageProps {
  step: number;
}

const Stage5Prompts: React.FC<Stage5Props> = ({
  onNext,
  onBack,
  isLoading,
}) => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [userPrompts, setUserPrompts] = useState<UserPromptAnswer[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoadingTemplates(true);
      try {
        const [temps, existing] = await Promise.all([
          profileService.getPromptTemplates(),
          profileService.getMyPrompts(),
        ]);
        setTemplates(temps);
        setUserPrompts(existing);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadData();
  }, []);

  const handleSelectTemplate = (template: PromptTemplate) => {
    const existing = userPrompts.find((p) => p.promptId === template.id);
    setSelectedTemplate(template);
    setCurrentAnswer(existing?.answer || '');
  };

  const handleSavePrompt = async () => {
    if (!selectedTemplate || !currentAnswer.trim()) return;

    const existing = userPrompts.find((p) => p.promptId === selectedTemplate.id);

    if (existing) {
      await profileService.updatePromptAnswer(existing.id, currentAnswer);
      setUserPrompts((prev) =>
        prev.map((p) =>
          p.id === existing.id ? { ...p, answer: currentAnswer } : p
        )
      );
    } else {
      const result = await profileService.addPromptAnswer(
        selectedTemplate.id,
        currentAnswer
      );
      if (result) {
        const newPrompt: UserPromptAnswer = {
          id: result.id,
          promptId: result.promptId,
          promptText: selectedTemplate.promptText,
          category: selectedTemplate.category,
          answer: result.answer,
        };
        setUserPrompts((prev) => [...prev, newPrompt]);
      }
    }

    setSelectedTemplate(null);
    setCurrentAnswer('');
  };

  const handleDeletePrompt = async (promptId: string) => {
    await profileService.deletePromptAnswer(promptId);
    setUserPrompts((prev) => prev.filter((p) => p.id !== promptId));
  };

  const handleNext = async () => {
    onNext({ prompts: userPrompts });
  };

  const getTemplatesByCategory = () => {
    const grouped: Record<string, PromptTemplate[]> = {};
    templates.forEach((template) => {
      if (!grouped[template.category]) {
        grouped[template.category] = [];
      }
      grouped[template.category].push(template);
    });
    return grouped;
  };

  if (loadingTemplates) {
    return (
      <div className="wizard-stage">
        <div className="wizard-loading">Loading prompts...</div>
      </div>
    );
  }

  const groupedTemplates = getTemplatesByCategory();
  const answeredCount = userPrompts.length;

  return (
    <div className="wizard-stage">
      <div className="wizard-step">
        <h3 className="wizard-step-title">Conversation Starters</h3>
        <p className="wizard-step-subtitle">
          Answer prompts to help others start conversations with you (
          {answeredCount} answered)
        </p>

        {selectedTemplate ? (
          <div className="wizard-prompt-editor">
            <div className="wizard-prompt-question">
              {selectedTemplate.promptText}
            </div>
            <textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              placeholder="Your answer..."
              rows={4}
              maxLength={500}
            />
            <div className="wizard-prompt-editor-actions">
              <button
                type="button"
                className="wizard-btn wizard-btn-secondary"
                onClick={() => {
                  setSelectedTemplate(null);
                  setCurrentAnswer('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="wizard-btn wizard-btn-primary"
                onClick={handleSavePrompt}
                disabled={!currentAnswer.trim()}
              >
                Save Answer
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Show answered prompts */}
            {userPrompts.length > 0 && (
              <div className="wizard-answered-prompts">
                <h4>Your Answers</h4>
                {userPrompts.map((prompt) => {
                  const template = templates.find((t) => t.id === prompt.promptId);
                  return (
                    <div key={prompt.id} className="wizard-answered-prompt">
                      <div className="answered-prompt-question">
                        {template?.promptText || 'Unknown prompt'}
                      </div>
                      <div className="answered-prompt-answer">{prompt.answer}</div>
                      <div className="answered-prompt-actions">
                        <button
                          type="button"
                          className="wizard-btn-icon"
                          onClick={() => template && handleSelectTemplate(template)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="wizard-btn-icon danger"
                          onClick={() => handleDeletePrompt(prompt.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Available prompts by category */}
            <div className="wizard-prompt-categories">
              <h4>Choose a Prompt</h4>
              {Object.entries(groupedTemplates).map(([category, temps]) => (
                <div key={category} className="wizard-prompt-category">
                  <h5>{category}</h5>
                  <div className="wizard-prompt-list">
                    {temps
                      .filter((t) => !userPrompts.find((p) => p.promptId === t.id))
                      .map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          className="wizard-prompt-item"
                          onClick={() => handleSelectTemplate(template)}
                        >
                          {template.promptText}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {answeredCount < 2 && (
          <p className="wizard-warning">
            We recommend answering at least 2 prompts to make your profile more
            engaging
          </p>
        )}
      </div>

      <div className="wizard-stage-actions">
        <button
          type="button"
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Complete Stage'}
        </button>
      </div>
    </div>
  );
};

export default Stage5Prompts;
